import { DateTime } from "luxon";
import { TimeFormatPreset, timeFormatPresets } from "./presets";

export type { TimeFormatPreset };

/**
 * Options type for the time formatter function
 */
export type TimeFormatterOptions = {
  /**
   * The locale to use for the time formatter
   * @default "en-US"
   */
  locale?: Intl.LocalesArgument;
  /**
   * The format preset or Intl.DateTimeFormatOptions object
   * - "short": 08:30 AM
   * - "medium": 08:30:00 AM
   * - Intl.DateTimeFormatOptions: Custom format (e.g., { hour: "2-digit", minute: "2-digit", hour12: false })
   * @default "short"
   */
  format?: TimeFormatPreset | Intl.DateTimeFormatOptions;
};

/**
 * Helper function for formatting table cells with times, including i18n support.
 * Treats time strings as "plain times" without timezone conversion.
 * @param value - The time value to format (ISO time string "HH:mm:ss" or "HH:mm")
 * @param options - The options for the time formatter
 * @returns The formatted time string or null for undefined/null values
 */
export const timeFormatter = (
  value?: string | null,
  { locale = "en-US", format = "short" }: TimeFormatterOptions = {},
) => {
  if (value == null) {
    return null;
  }

  // Parse time-only string by prepending a reference date
  // Use UTC zone to treat as a "plain time" without timezone conversion
  const dateTime = DateTime.fromISO(`1970-01-01T${value}`, { zone: "utc" });

  if (!dateTime.isValid) {
    return null;
  }

  // Convert locale to string for Luxon compatibility
  const localeString = locale?.toString() ?? "en-US";

  // Check if format is a preset string or Intl options object
  if (typeof format === "string") {
    const preset = timeFormatPresets[format];
    return dateTime.setLocale(localeString).toLocaleString(preset);
  }

  // Custom Intl.DateTimeFormatOptions
  return dateTime.setLocale(localeString).toLocaleString(format);
};
